import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Ruler, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { categoryLabel } from "@/lib/measurementCategoryMap";
import CategoryIcon from "@/components/CategoryIcon";

/**
 * Iter57.2 (P2) — Property measurement history / reuse panel.
 *
 * Reads existing measurements for a customer's property via
 * `GET /api/measurements?property_address=<addr>&include_usage=true`
 * (backend enriches with `linked_estimates`). Renders a card list of
 * saved measurements grouped by category with:
 *   - Measured-on date
 *   - "Used on" chips linking to each estimate (click-through)
 *   - Delete action (409-aware — reuses the workspace 409 pattern)
 *
 * Deliberately does NOT create a duplicate storage layer — it consumes
 * the same `measurements` + `measurement_links` collections the Google
 * Workspace writes to.
 *
 * Navigation is delegated to the caller via `onOpenEstimate(estimateId)` so
 * this component has no direct dependency on react-router. CustomerProfile
 * wires it to `nav(`/app/estimates/${id}`)`.
 */
export default function PropertyMeasurementsSection({ customer, onOpenEstimate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const address = customer?.address || "";

  const load = async () => {
    if (!address) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/measurements?property_address=${encodeURIComponent(address)}&include_usage=true`
      );
      setItems(data || []);
    } catch {
      // Non-fatal — the section just shows empty.
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [address]); // eslint-disable-line

  const grouped = useMemo(() => {
    const g = {};
    for (const m of items) {
      const key = m.service_category || "other";
      (g[key] = g[key] || []).push(m);
    }
    return g;
  }, [items]);

  const del = async (m) => {
    if (!window.confirm(`Delete measurement "${categoryLabel(m.service_category) || m.measurement_type}"?`)) return;
    try {
      await api.delete(`/measurements/${m.id}`);
      setItems((xs) => xs.filter((x) => x.id !== m.id));
      toast.success("Measurement deleted");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error_kind === "measurement_linked") {
        const ids = detail.linked_estimate_ids || [];
        toast.error(
          `Can't delete — measurement is in use. Linked to ${ids.length} estimate${ids.length === 1 ? "" : "s"}. Unlink first or delete the estimate line.`,
          { duration: 6000 }
        );
      } else {
        toast.error("Delete failed");
      }
    }
  };

  if (!address) return null;

  return (
    <div
      className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp"
      data-testid="property-measurements-section"
    >
      <div className="flex items-center gap-2 mb-4">
        <Ruler className="w-5 h-5 text-[#FF5A00]" />
        <h2 className="font-heading text-xl font-bold">Measurements on this property</h2>
        <span className="ml-auto text-xs text-zinc-500 font-mono-accent" data-testid="property-measurements-count">
          {items.length}
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-3" data-testid="property-measurements-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-3" data-testid="property-measurements-empty">
          No saved measurements yet. Open <em>Smart Estimator → Measure on map</em> on any estimate for this property to save one — you'll be able to reuse it here.
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 mb-2">
                {categoryLabel(cat)} · {list.length}
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                {list.map((m) => (
                  <MeasurementRow
                    key={m.id}
                    measurement={m}
                    onDelete={() => del(m)}
                    onOpenEstimate={onOpenEstimate || (() => {})}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MeasurementRow({ measurement: m, onDelete, onOpenEstimate }) {
  const value = m.square_feet != null ? `${Math.round(m.square_feet).toLocaleString()} sq ft`
    : m.linear_feet != null ? `${Math.round(m.linear_feet).toLocaleString()} ft`
    : m.metric_value != null ? `${Math.round(m.metric_value)} m`
    : "—";
  const measuredOn = m.created_at ? new Date(m.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";
  const usage = m.linked_estimates || [];
  return (
    <div
      className="border border-zinc-200 rounded-lg p-3 hover:border-[#FF5A00] transition-colors"
      data-testid={`property-measurement-row-${m.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5 min-w-0 flex-1">
          <div
            className="w-9 h-9 shrink-0 rounded-md bg-orange-50 border border-orange-200 text-[#FF5A00] flex items-center justify-center"
            data-testid={`property-measurement-icon-${m.id}`}
          >
            <CategoryIcon category={m.service_category} className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate leading-tight">
              {m.notes ? m.notes : `${categoryLabel(m.service_category) || "Measurement"} area`}
            </div>
            <div className="text-xs text-zinc-500 font-mono-accent mt-0.5">
              {categoryLabel(m.service_category)} · {value}
              {m.service_category === "solar" && m.solar_metadata?.panel_count != null && (
                <span data-testid={`property-measurement-panels-${m.id}`}> · {m.solar_metadata.panel_count} panels</span>
              )}
              {measuredOn && <span> · Measured {measuredOn}</span>}
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-zinc-400 hover:text-red-600 shrink-0 p-1 rounded"
          title="Delete measurement"
          data-testid={`property-measurement-delete-${m.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {usage.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          <span className="text-[10px] uppercase tracking-widest font-mono-accent text-zinc-500 mr-1">
            Used on
          </span>
          {usage.map((e) => (
            <button
              key={e.id}
              onClick={() => onOpenEstimate(e.id)}
              className="text-xs px-2 py-0.5 border border-zinc-200 rounded hover:border-[#FF5A00] hover:bg-orange-50 flex items-center gap-1"
              data-testid={`property-measurement-usage-${m.id}-${e.id}`}
              title={e.title || e.estimate_number || e.id}
            >
              {e.estimate_number || `#${e.id.slice(-6)}`}
              <ArrowRight className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
